import type { Project, Workspace } from "@racler/protocol";
import { projectPath } from "./routes.ts";

export const ADD_PROJECT_OPTION = "__add__";

/**
 * Filters projects belonging to the specified Workspace.
 * When workspaceId is null or empty, returns empty list (enforcing cascading constraint).
 */
export function filterProjectsByWorkspace(
  projects: Project[] | null | undefined,
  workspaceId: string | null | undefined,
): Project[] {
  if (!projects || !workspaceId) return [];
  return projects.filter((p) => p.workspaceId === workspaceId);
}

/**
 * Resolves the currently active Workspace id:
 * 1. If an active Project exists and belongs to a known Workspace, that Workspace is active.
 * 2. If a Workspace was manually selected (e.g. on /m/:id) and exists on the machine, keep it.
 * 3. Otherwise fall back to the first available Workspace on the machine, or null.
 */
export function resolveActiveWorkspaceId(params: {
  workspaces: Workspace[] | null | undefined;
  projects?: Project[] | null | undefined;
  projectId?: string | undefined;
  selectedWorkspaceId?: string | null | undefined;
}): string | null {
  const { workspaces, projects, projectId, selectedWorkspaceId } = params;
  if (!workspaces || workspaces.length === 0) return null;

  if (projectId && projects) {
    const prj = projects.find((p) => p.id === projectId);
    if (prj && workspaces.some((w) => w.id === prj.workspaceId)) {
      return prj.workspaceId;
    }
  }

  if (selectedWorkspaceId && workspaces.some((w) => w.id === selectedWorkspaceId)) {
    return selectedWorkspaceId;
  }

  return workspaces[0]?.id ?? null;
}

/**
 * Checks whether the current Project belongs to the target Workspace.
 */
export function projectBelongsToWorkspace(
  project: Project | undefined | null,
  workspaceId: string | null | undefined,
): boolean {
  if (!project || !workspaceId) return false;
  return project.workspaceId === workspaceId;
}

/**
 * Calculates navigation target when selecting a Workspace:
 * - If current project already belongs to the new Workspace, no route change needed (returns null).
 * - Otherwise navigating to the machine root (/m/:machineId) removes the invalid Project.
 */
export function targetUrlForWorkspaceChange(
  machineId: string,
  targetWorkspaceId: string,
  currentProject: Project | undefined | null,
): string | null {
  if (currentProject && currentProject.workspaceId === targetWorkspaceId) {
    return null;
  }
  return `/m/${machineId}`;
}

/**
 * Calculates navigation target when selecting a Project:
 * - ADD_PROJECT_OPTION opens Settings Project add page.
 * - Valid project id opens /m/:machineId/p/:projectId.
 */
export function targetUrlForProjectChange(
  machineId: string,
  targetProjectId: string,
): string {
  if (targetProjectId === ADD_PROJECT_OPTION) {
    return `/settings/projects?add=${machineId}`;
  }
  return projectPath(machineId, targetProjectId);
}
